import { useCallback, useEffect } from "react";
import { DataSet } from "vis-data";
import { Timeline } from "vis-timeline/esnext";
import {
  selectAvailabilityStartTime,
  selectComputedAvailabilityEndTime,
  selectFromTime,
  selectIsLive,
  selectSegments,
  selectToTime,
} from "@/entities/player";
import { useAppSelector } from "@/app/appStore";

export function useTimelineBackground({
  dataset,
  timelineRef,
}: {
  dataset: DataSet<any, any>;
  timelineRef: React.RefObject<Timeline>;
}) {
  const toTime = useAppSelector(selectToTime);
  const isLive = useAppSelector(selectIsLive);
  const fromTime = useAppSelector(selectFromTime);
  const segments = useAppSelector(selectSegments);
  const availabilityStartTime = useAppSelector(selectAvailabilityStartTime);
  const computedAvailabilityEndTime = useAppSelector(
    selectComputedAvailabilityEndTime
  );

  const clearSegments = useCallback(() => {
    const currentSegments = dataset
      .getIds()
      .filter((id) => id.toString().startsWith("segment-"));

    dataset.remove(currentSegments);
  }, [dataset]);

  const addSegments = useCallback(() => {
    const segmentDataset = segments.map((segment) => ({
      id: `segment-${segment.i}`,
      start: segment.from,
      end: segment.to,
      type: "background",
    }));

    dataset.add(segmentDataset);
  }, [dataset, segments]);

  const clearBoundaries = useCallback(() => {
    const boundaries = ["filter-start", "filter-end", "START", "END"];

    const currentSegments = dataset
      .getIds()
      .filter((id) => boundaries.includes(id.toString()));

    dataset.remove(currentSegments);

    try {
      timelineRef.current?.removeCustomTime("filter-start");
      timelineRef.current?.removeCustomTime("filter-end");
    } catch {
      console.warn("Custom time not found");
    }
  }, [dataset, timelineRef]);

  const addBoundaries = useCallback(
    (
      fromTime: string,
      toTime: string,
      availabilityStartTime: number,
      computedAvailabilityEndTime: number
    ) => {
      timelineRef.current?.addCustomTime(fromTime, "filter-start");
      timelineRef.current?.addCustomTime(toTime, "filter-end");
      timelineRef.current?.setCustomTimeMarker("From", "filter-start", true);
      timelineRef.current?.setCustomTimeMarker("To", "filter-end", true);

      dataset.add([
        {
          id: "START",
          start: new Date(0),
          end: availabilityStartTime,
          type: "background",
        },
        {
          id: "END",
          start: computedAvailabilityEndTime,
          end: new Date(100_000_000_000_000),
          type: "background",
        },
      ]);
    },
    [timelineRef, dataset]
  );

  useEffect(() => {
    if (isLive) {
      clearSegments();
      return;
    }

    clearSegments();
    addSegments();
  }, [clearSegments, addSegments, isLive]);

  useEffect(() => {
    if (isLive) {
      dataset.clear();
      return;
    }

    if (
      fromTime &&
      toTime &&
      availabilityStartTime &&
      computedAvailabilityEndTime &&
      !isLive
    ) {
      clearBoundaries();
      addBoundaries(
        fromTime,
        toTime,
        availabilityStartTime,
        computedAvailabilityEndTime
      );
      return;
    }

    clearBoundaries();
  }, [
    dataset,
    availabilityStartTime,
    computedAvailabilityEndTime,
    fromTime,
    toTime,
    isLive,
    clearBoundaries,
    addBoundaries,
  ]);
}
